import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

/*
 * Display units.
 *
 * The workbook records velocity in metres per second, temperature in Celsius
 * and coordinates in millimetres, because that is what the electronic target
 * writes. Australian fullbore shooters do not read them that way: velocity is
 * quoted in feet per second, temperature in Celsius, and group size in inches
 * or MOA. So the stored values are left exactly as imported and converted only
 * when they are printed.
 *
 * The three are INDEPENDENT settings, not one metric/imperial switch. A single
 * switch would force Fahrenheit on anyone who wanted feet per second, which is
 * the combination almost nobody uses. Defaults are the common case: fps, °C,
 * and MOA.
 *
 * The choice is kept in localStorage rather than on the account, so it works
 * for the public site where nobody is signed in.
 */

export type VelocityUnit = "fps" | "m/s";
export type TempUnit = "C" | "F";
/** MOA is angular, so it holds at any distance; mm and inch are the physical measure. */
export type LengthUnit = "MOA" | "inch" | "mm";

export interface UnitPrefs {
  velocity: VelocityUnit;
  temp: TempUnit;
  length: LengthUnit;
}

const DEFAULTS: UnitPrefs = { velocity: "fps", temp: "C", length: "MOA" };
const KEY = "srp_units";

const read = (): UnitPrefs => {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || "{}") };
  } catch {
    return DEFAULTS;
  }
};

const UnitContext = createContext<{ units: UnitPrefs; setUnit: <K extends keyof UnitPrefs>(k: K, v: UnitPrefs[K]) => void }>({
  units: DEFAULTS,
  setUnit: () => {},
});

export function UnitProvider({ children }: { children: React.ReactNode }) {
  const [units, setUnits] = useState<UnitPrefs>(read);
  const setUnit = useCallback(<K extends keyof UnitPrefs>(k: K, v: UnitPrefs[K]) => {
    setUnits((prev) => {
      const next = { ...prev, [k]: v };
      try {
        localStorage.setItem(KEY, JSON.stringify(next));
      } catch {
        /* a browser refusing storage must not break the page */
      }
      return next;
    });
  }, []);
  const value = useMemo(() => ({ units, setUnit }), [units, setUnit]);
  return <UnitContext.Provider value={value}>{children}</UnitContext.Provider>;
}

export const useUnits = () => useContext(UnitContext);

/* ------------------------------- Conversions ------------------------------- */

const MS_TO_FPS = 3.280839895;
const MM_PER_INCH = 25.4;

/** Velocity as stored (m/s) rendered in the chosen unit. */
export const formatVelocity = (ms: number | null | undefined, unit: VelocityUnit, withUnit = false): string => {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "—";
  const v = unit === "fps" ? ms * MS_TO_FPS : ms;
  // fps figures run to four digits, so a decimal place there is noise.
  const text = unit === "fps" ? String(Math.round(v)) : v.toFixed(1);
  return withUnit ? `${text} ${unit}` : text;
};

/*
 * A spread (SD, extreme spread) scales like the measurement itself, so it uses
 * the same factor — but it is a difference, not a reading, and 3 fps of SD is
 * worth a decimal place where 800 fps is not.
 */
export const formatVelocitySpread = (ms: number | null | undefined, unit: VelocityUnit, withUnit = false): string => {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "—";
  const v = unit === "fps" ? ms * MS_TO_FPS : ms;
  const text = v.toFixed(1);
  return withUnit ? `${text} ${unit}` : text;
};

export const velocityLabel = (unit: VelocityUnit) => unit;

/** Temperature as stored (°C) rendered in the chosen unit. */
export const formatTemp = (c: number | null | undefined, unit: TempUnit, withUnit = true): string => {
  if (c === null || c === undefined || !Number.isFinite(c)) return "—";
  const t = unit === "F" ? c * 1.8 + 32 : c;
  return withUnit ? `${t.toFixed(1)}°${unit}` : t.toFixed(1);
};

/**
 * A length on the target. MOA comes straight from the plotted coordinate;
 * inch and mm need the face's scale, which is why `mmPerMoa` is required for
 * anything other than MOA — without it the value can only be shown in MOA.
 */
export const formatLength = (
  moa: number | null | undefined,
  unit: LengthUnit,
  mmPerMoa?: number | null,
  withUnit = false
): string => {
  if (moa === null || moa === undefined || !Number.isFinite(moa)) return "—";
  if (unit === "MOA" || !mmPerMoa) return withUnit ? `${moa.toFixed(2)} MOA` : moa.toFixed(2);
  const mm = moa * mmPerMoa;
  if (unit === "mm") return withUnit ? `${mm.toFixed(1)} mm` : mm.toFixed(1);
  const inches = mm / MM_PER_INCH;
  return withUnit ? `${inches.toFixed(2)} in` : inches.toFixed(2);
};

/** What to put in a column heading for the chosen length unit. */
export const lengthLabel = (unit: LengthUnit, mmPerMoa?: number | null) =>
  unit === "MOA" || !mmPerMoa ? "MOA" : unit === "mm" ? "mm" : "in";
